import type { FilmMetadata } from "../types/media";

const TECHNICAL_VALUES = new Set([
  "undefined",
  "null",
  "nan",
  "n/a",
  "none"
]);

const ROLE_LABELS_RU: Record<string, string> = {
  Actor: "Актёр",
  Actress: "Актриса",
  Composer: "Композитор",
  Director: "Режиссёр",
  Editor: "Монтажёр",
  "Executive Producer": "Исполнительный продюсер",
  "Film Editor": "Монтажёр",
  "Original Music Composer": "Композитор",
  Producer: "Продюсер",
  Self: "В роли себя",
  "Director of Photography": "Оператор-постановщик",
  "Screenplay": "Сценарист",
  Story: "Автор сюжета",
  Writer: "Сценарист"
};

const ROLE_LABELS_EN = Object.fromEntries(
  Object.entries(ROLE_LABELS_RU).map(([english, russian]) => [russian, english])
);

Object.assign(ROLE_LABELS_EN, {
  Монтажёр: "Editor",
  Композитор: "Composer",
  Сценарист: "Writer"
});

const hasCyrillic = (value: string) => /[\u0400-\u04FF]/.test(String(value || ""));

const normalizeText = (value: unknown) => {
  if (value == null || (typeof value === "number" && !Number.isFinite(value))) return "";

  const text = String(value)
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return TECHNICAL_VALUES.has(text.toLowerCase()) ? "" : text;
};

export const hasDisplayValue = (value: unknown) => Boolean(normalizeText(value));

export const displayText = (value: unknown, fallback = "Нет данных") =>
  normalizeText(value) || fallback;

export const displayYear = (value: unknown, fallback = "Год неизвестен") => {
  const text = normalizeText(value);
  const match = text.match(/\b(18\d{2}|19\d{2}|20\d{2}|21\d{2}|22\d{2})\b/);
  return match ? match[1] : fallback;
};

export const displayDate = (value: unknown, locale = "ru-RU", fallback = "Дата неизвестна") => {
  const text = normalizeText(value);
  if (!text) return fallback;

  const date = value instanceof Date ? value : new Date(text);
  return Number.isNaN(date.getTime()) ? fallback : date.toLocaleDateString(locale);
};

export const splitReadableParagraphs = (value: unknown, fallback = "Нет данных") => {
  const rawText = value == null
    ? ""
    : String(value).replace(/[\u200B-\u200D\uFEFF]/g, "").trim();
  const text = hasDisplayValue(rawText) ? rawText : fallback;
  const explicitParagraphs = text
    .split(/\n\s*\n+/)
    .map((paragraph) => normalizeText(paragraph))
    .filter(Boolean);

  if (explicitParagraphs.length > 1 || text === fallback) return explicitParagraphs;

  const normalizedText = normalizeText(text);
  const sentences = normalizedText.match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g) || [normalizedText];
  const paragraphs: string[] = [];
  for (let index = 0; index < sentences.length; index += 3) {
    paragraphs.push(sentences.slice(index, index + 3).join(" ").replace(/\s+/g, " ").trim());
  }

  return paragraphs.filter(Boolean);
};

const translateRolePart = (value: string, language: string, fallback: string) => {
  const role = normalizeText(value);
  if (!role) return role;
  if (String(language).toLowerCase().startsWith("ru")) {
    return ROLE_LABELS_RU[role] || (hasCyrillic(role) ? role : fallback);
  }
  return hasCyrillic(role) ? ROLE_LABELS_EN[role] || fallback : role;
};

export const displayCreditRole = (
  value: unknown,
  { language = "ru", kind = "crew", fallback }: { language?: string; kind?: "cast" | "crew"; fallback?: string } = {}
) => {
  const defaultFallback = String(language).toLowerCase().startsWith("ru")
    ? kind === "cast"
      ? "Роль не указана"
      : "Участие не указано"
    : kind === "cast"
      ? "Role not specified"
      : "Participation not specified";
  const text = normalizeText(value);
  if (!text) return fallback || defaultFallback;

  const translated = text
    .split(/(\s*[,/;]\s*)/)
    .map((part) => /[,/;]/.test(part) ? part : translateRolePart(part, language, defaultFallback))
    .join("")
    .trim();

  return translated || fallback || defaultFallback;
};

export const getSafeTitle = (source: FilmMetadata | null = {}, fallback = "Без названия") =>
  [
    source?.title,
    source?.name,
    source?.filmTitle,
    source?.original_title,
    source?.original_name
  ].map(normalizeText).find(Boolean) || fallback;
